import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getImageSource } from "~/utils/default-image";
import { getProductDetailsUrl } from "~/utils/routes";
import { VariantCard } from "./variant-card.component";

export type VariantItem = {
	id: string;
	name: string;
	kind: string | null;
	imageUrl: string | null;
	category: string | null;
	price: number;
	isAvailable: boolean;
	isAvailableAtCartVendor: boolean | null;
	label: string;
	sortValue: number;
};

type VariantSelectorProps = {
	currentProductId: string;
	variants: VariantItem[];
	preferredVendorId?: string;
};

/**
 * Horizontally-scrollable row of variant image cards.
 * Price deltas are computed in-memory relative to the currently selected variant.
 */
export function VariantSelector({
	currentProductId,
	variants,
	preferredVendorId,
}: VariantSelectorProps) {
	const { t } = useTranslation("product");
	const navigate = useNavigate();

	if (variants.length <= 1) return null;

	const sorted = [...variants].sort((a, b) => a.sortValue - b.sortValue);
	const selectedVariant = sorted.find((v) => v.id === currentProductId);
	const basePrice = selectedVariant?.price ?? 0;

	const handleVariantClick = (variant: VariantItem) => {
		if (variant.id === currentProductId) return;
		const url = getProductDetailsUrl(variant.id, variant.name, variant.kind);
		navigate(preferredVendorId ? `${url}?vendorId=${preferredVendorId}` : url);
	};

	return (
		<div className="flex flex-col gap-2" data-testid="variant-selector">
			<span className="text-body-sm text-gray-500 tracking-[-0.02em]">
				{t("variantSelectorLabel")}
			</span>

			<div className="flex gap-2 overflow-x-auto pb-1">
				{sorted.map((variant) => (
					<VariantCard
						key={variant.id}
						variant={{
							...variant,
							imageUrl: getImageSource(variant.imageUrl, variant.category),
						}}
						isSelected={variant.id === currentProductId}
						priceDeltaCents={variant.price - basePrice}
						label={variant.label}
						onClick={() => handleVariantClick(variant)}
					/>
				))}
			</div>
		</div>
	);
}
